import { useEffect } from "react";
import { FormularioTablaDesarrolloDto } from "../models/formularioTablaDesarrollo";
import { Box, Dialog, DialogContent, DialogTitle, Grid2 } from "@mui/material";
import MButton from "../../../../components/inputs/yup/materialui/mButton";
import InputSelect from "../../../../components/inputs/yup/materialui/inputSelect";
import InputNumber from "../../../../components/inputs/yup/materialui/inputNumber";
import InputFecha from '../../../../components/inputs/yup/materialui/inputFecha';
import InputText from "../../../../components/inputs/yup/materialui/inputText";
import * as Yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, UseFormWatch } from "react-hook-form";
import { selectorTransversalNumerico } from "../../../../dtos/selector";
import { useOperacion } from "../../../detalleMutuo/hooks/useOperacion";
import { operacion } from "../../../detalleMutuo/models/operacion";
import { TipoProducto, TipoProductoValores } from "../../../../utils/enums";

interface IProps {
    show: boolean;
    onHide: () => void;
    formularioDefecto?: FormularioTablaDesarrolloDto | null
    watchParentForm: UseFormWatch<operacion> | null;
    onCreate: (e: FormularioTablaDesarrolloDto) => void;
    onUpdate: (e: FormularioTablaDesarrolloDto) => void;
    idMutuo: number | null;
    asignarACuotaMinima: boolean | null;
}

const ModalGeneracionTablaDesarrollo = ({ show, onHide, formularioDefecto, watchParentForm, onCreate, onUpdate, idMutuo, asignarACuotaMinima }: IProps) => {

    const periodosAnio: selectorTransversalNumerico[] = [
        { label: "Anual (1 cuota al año)", value: 1 },
        { label: "Semestral (2 cuotas al año)", value: 2 },
        { label: "Cuatrimestral (3 cuotas al año)", value: 3 },
        { label: "Trimestral (4 cuotas al año)", value: 4 },
        { label: "Bimestral (6 cuotas al año)", value: 6 },
        { label: "Mensual (12 cuotas al año)", value: 12 }
    ]

    const schema = Yup.object<FormularioTablaDesarrolloDto>().shape({
        fechaProtocolizacion: Yup.date().optional().nullable(),
        numeroRepertorio: Yup.string().optional().nullable(),
        numeroProtocolizacion: Yup.string().optional().nullable(),
        notaria: Yup.string().optional().nullable(),
        plazoAnual: Yup.number().required("Campo requerido").min(1, "El plazo anual debe ser mayor a 0"),
        tasaAnual: Yup.number().required("Campo requerido").min(1, "La tasa anual debe ser mayor a 0"),
        periodoAnio: Yup.number().optional().nullable(),
        periodoGraciaK: Yup.number().required("Campo requerido").min(0, "El periodo de gracia k no puede ser menor a 0"),
        periodoGraciaKI: Yup.number().required("Campo requerido").min(0, "El periodo de gracia k + i no puede ser menor a 0"),
        tipoProducto: Yup.string().required("Campo requerido"),
        emisor: Yup.string().required("Campo requerido"),
        nombre: Yup.string().optional().nullable(),
        plazoDivReducido: Yup.number().when('tipoProducto', {
            is: TipoProductoValores.MHD,
            then: (schema) => schema.required("Campo requerido").min(0, "El plazo div reducido no puede ser menor a 0"),
            otherwise: (schema) => schema.optional().nullable()
        }),
        porcAmortizacion: Yup.number().when('tipoProducto', {
            is: TipoProductoValores.MHD,
            then: (schema) => schema.required("Campo requerido").min(0, "El porcentaje de amortización no puede ser menor a 0"),
            otherwise: (schema) => schema.optional().nullable()
        })
    });

    const {
        control,
        formState: { errors, isDirty },
        reset,
        watch,
        handleSubmit,
        setError,
        setValue
    } = useForm<FormularioTablaDesarrolloDto>({
        resolver: yupResolver(schema),
        mode: 'onChange',
    })

    const watcher = watch();
    const watchParentFormData = watchParentForm !== null ? watchParentForm() : null;
    const isWatchParentFormDataValid = watchParentFormData !== null;

    const { listarSelectores, getEmisores, tipoProductos, emisores } = useOperacion();

    const loadFormulario = async () => {
        await listarSelectores();
        await getEmisores();
        setValue("fechaProtocolizacion", undefined, { shouldDirty: true, shouldValidate: true })
        if (formularioDefecto) {
            const fechaDefecto = formularioDefecto.fechaProtocolizacion
            const listaTipoProductos = tipoProductos.map((x) => x.value)
            if (!listaTipoProductos.includes(formularioDefecto.tipoProducto)) {
                formularioDefecto.tipoProducto = "0";
            }
            reset(formularioDefecto)
            setValue("fechaProtocolizacion", fechaDefecto, { shouldDirty: true, shouldValidate: true }) //Esto es debido a como opera el input fecha 
        }
        else {
            const formularioInicial: FormularioTablaDesarrolloDto = {
                fechaProtocolizacion: undefined,
                numeroRepertorio: "",
                numeroProtocolizacion: "",
                notaria: "",
                plazoAnual: 0,
                tasaAnual: 0,
                periodoAnio: undefined,
                periodoGraciaK: 0,
                periodoGraciaKI: 0,
                tipoProducto: "",
                emisor: emisores.length > 0 ? emisores[0].value : "",
                nombre: null,
                plazoDivReducido: 5,
                porcAmortizacion: 1
            }
            if (isWatchParentFormDataValid) {
                formularioInicial.tasaAnual = watchParentFormData.tasaEmision
                formularioInicial.idMutuo = idMutuo
                formularioInicial.asignarACuotaMinima = asignarACuotaMinima
            }
            reset(formularioInicial);
        }
    }

    useEffect(() => {
        loadFormulario();
    }, [formularioDefecto, show])

    const onSubmit = (formulario: FormularioTablaDesarrolloDto) => {

        if (formulario.periodoAnio === null || formulario.periodoAnio === undefined || formulario.periodoAnio === 0) {
            setError("periodoAnio", { type: 'manual', message: 'Debe indicar un periodo' })
            return
        }

        if (formularioDefecto) {
            let isValid: boolean = true;
            if (formulario.fechaProtocolizacion === null || formulario.fechaProtocolizacion === undefined) {
                setError("fechaProtocolizacion", { type: 'manual', message: 'Debe indicar una fecha de protocolización' })
                isValid = false;
            }
            if (formulario.numeroRepertorio === null || formulario.numeroRepertorio === undefined || formulario.numeroRepertorio === "") {
                setError("numeroRepertorio", { type: 'manual', message: 'Debe indicar un número de repertorio' })
                isValid = false;
            }
            if (formulario.numeroProtocolizacion === null || formulario.numeroProtocolizacion === undefined || formulario.numeroProtocolizacion === "") {
                setError("numeroProtocolizacion", { type: 'manual', message: 'Debe indicar un número de protocolización' })
                isValid = false;
            }
            if (formulario.notaria === null || formulario.notaria === undefined || formulario.notaria === "") {
                setError("notaria", { type: 'manual', message: 'Debe indicar una notaría' })
                isValid = false;
            }

            if (isValid) {
                onUpdate(formulario)
            }

            return;
        }
        onCreate(formulario);
    }
    return (
        <Dialog
            open={show}
            onClose={onHide}
            maxWidth='xl'
        >
            <DialogTitle>
                {(formularioDefecto ? "Editar " : " Generar") + " tabla de desarrollo"}
            </DialogTitle>
            <DialogContent>
                <Grid2 container spacing={2} sx={{ marginTop: 1 }}>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputFecha
                            name="fechaProtocolizacion"
                            control={control}
                            label="Fecha de protocolización"
                            error={errors.fechaProtocolizacion}
                            fecha={watcher.fechaProtocolizacion}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputText
                            name={"numeroRepertorio"}
                            control={control}
                            label="Número repertorio"
                            error={errors.numeroRepertorio}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputText
                            name={"numeroProtocolizacion"}
                            control={control}
                            label="Número protocolización"
                            error={errors.numeroProtocolizacion}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputText
                            name={"notaria"}
                            control={control}
                            label="Notaría"
                            error={errors.notaria}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputNumber
                            name={"plazoAnual"}
                            control={control}
                            decimales={0}
                            label="Plazo anual"
                            error={errors.plazoAnual}
                            minValue={0}
                            readonly={formularioDefecto != null || formularioDefecto != undefined}
                            disabled={formularioDefecto != null || formularioDefecto != undefined}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputNumber
                            name={"tasaAnual"}
                            control={control}
                            decimales={2}
                            label="Tasa anual"
                            error={errors.tasaAnual}
                            minValue={0}
                            readonly={formularioDefecto != null || formularioDefecto != undefined || isWatchParentFormDataValid}
                            disabled={formularioDefecto != null || formularioDefecto != undefined || isWatchParentFormDataValid}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputSelect
                            label="Periodos al año"
                            name={'periodoAnio'}
                            control={control}
                            options={periodosAnio}
                            error={errors.periodoAnio}
                            readonly={formularioDefecto != null || formularioDefecto != undefined}
                            disabled={formularioDefecto != null || formularioDefecto != undefined}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputNumber
                            name={"periodoGraciaK"}
                            control={control}
                            decimales={0}
                            label="Período gracia k"
                            error={errors.periodoGraciaK}
                            minValue={0}
                            readonly={formularioDefecto != null || formularioDefecto != undefined}
                            disabled={formularioDefecto != null || formularioDefecto != undefined}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputNumber
                            name={"periodoGraciaKI"}
                            control={control}
                            decimales={0}
                            label="Período gracia k+i"
                            error={errors.periodoGraciaKI}
                            minValue={0}
                            readonly={formularioDefecto != null || formularioDefecto != undefined}
                            disabled={formularioDefecto != null || formularioDefecto != undefined}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputSelect
                            label="Tipo producto"
                            name={'tipoProducto'}
                            control={control}
                            options={tipoProductos}
                            error={errors.tipoProducto}
                            readonly={formularioDefecto != null || formularioDefecto != undefined}
                            disabled={formularioDefecto != null || formularioDefecto != undefined}
                        />
                    </Grid2>
                    {watcher.tipoProducto === TipoProducto.MHD &&
                        <>
                            <Grid2 size={{ xs: 12, lg: 3 }}>
                                <InputNumber
                                    name={"plazoDivReducido"}
                                    control={control}
                                    decimales={0}
                                    label="Plazo div reducido (años)"
                                    error={errors.plazoDivReducido}
                                    minValue={0}
                                    readonly={formularioDefecto != null || formularioDefecto != undefined}
                                    disabled={formularioDefecto != null || formularioDefecto != undefined}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, lg: 3 }}>
                                <InputNumber
                                    name={"porcAmortizacion"}
                                    control={control}
                                    decimales={4}
                                    label="Porc amortización"
                                    error={errors.porcAmortizacion}
                                    minValue={0}
                                    maxValue={99}
                                    prefix={"%"}
                                    readonly={formularioDefecto != null || formularioDefecto != undefined}
                                    disabled={formularioDefecto != null || formularioDefecto != undefined}
                                />
                            </Grid2>
                        </>

                    }

                    {
                        formularioDefecto != null || formularioDefecto != undefined ?
                            <Grid2 size={{ xs: 12, lg: 3 }}>
                                <InputText
                                    name={"nombre"}
                                    control={control}
                                    label="Nombre"
                                    error={errors.nombre}
                                    desabilitado
                                    readonly
                                />
                            </Grid2>
                            : <></>
                    }
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputSelect
                            label="Emisor"
                            name={'emisor'}
                            control={control}
                            readonly
                            disabled
                            options={emisores}
                            error={errors.emisor}
                        />
                    </Grid2>
                </Grid2>
                <Grid2 size={4} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: "15px" }}>
                    <MButton onClick={onHide} variant="contained" color="inherit">
                        Cancelar
                    </MButton>
                    <MButton onClick={handleSubmit(onSubmit)} variant="contained" color="primary" style={{ marginLeft: "10px" }}>
                        {formularioDefecto ? 'Editar' : 'Agregar'}
                    </MButton>
                </Grid2>
            </DialogContent>
        </Dialog>
    )

}

export default ModalGeneracionTablaDesarrollo;